import { commercial_goods } from '../utils';

/**
 * WISE Web Service - Product 95: COMMERCIO_PERSONA_FISICA
 * Validation Functions
 *
 * This module contains all validation functions for Product 95.
 */

import {
  INCOMPATIBLE_GUARANTEES,
  REQUIRED_GUARANTEE_COMBINATIONS,
  GUARANTEE_PARAMETERS,
  CAPITAL_LIMITS,
  ASSET_RULES,
  //   REQUIRED_TECHNICAL_DATA,
  REQUIRED_POLICY_VARIABLES,
  CONTRACT_CONSTANTS,
  ERROR_TYPES,
  ERROR_MESSAGES,
//   VALIDATION_PRIORITY,
} from './validationRulesProduct95';

function getGuaranteeDisplayName(code) {
  const found = commercial_goods.find((g) => g.id === code);
  if (found) return `${found.name} (${found.id})`;
  return code;
}

// ============================================================================
// VALIDATION RESULT STRUCTURE
// ============================================================================

class ValidationResult {
  constructor() {
    this.isValid = true;
    this.errors = [];
    this.warnings = [];
    this.suggestions = [];
  }

  addError(code, message, type = ERROR_TYPES.BLOCKING, details = {}) {
    // Translate English error codes to Italian for consistency
    const codeTranslations = {
      INVALID_PROPERTY_TYPE: 'TIPOLOGIA IMMOBILE NON VALIDA',
      ASSET_MISSING_REQUIRED_GUARANTEE: 'ASSET MANCANTE GARANZIA RICHIESTA',
      ASSET_GUARANTEE_NOT_ALLOWED: 'ASSET GARANZIA NON CONSENTITA',
      CAPITAL_BELOW_MIN: 'CAPITALE SOTTO IL MINIMO',
      CAPITAL_ABOVE_MAX: 'CAPITALE SOPRA IL MASSIMO',
      INCOMPATIBLE_GUARANTEES: 'GARANZIE INCOMPATIBILI',
      DUPLICATE_GUARANTEE: 'GARANZIA DUPLICATA',
      MISSING_REQUIRED_GUARANTEE: 'GARANZIA RICHIESTA MANCANTE',
      INVALID_PARAMETER_VALUE: 'VALORE PARAMETRO NON VALIDO',
      INVALID_EFFECTIVE_DATE: 'DATA DECORRENZA NON VALIDA',
      INVALID_EXPIRY_DATE: 'DATA SCADENZA NON VALIDA',
      INVALID_INCLUSION_DATE: 'DATA INCLUSIONE NON VALIDA',
      INVALID_EXCLUSION_DATE: 'DATA ESCLUSIONE NON VALIDA',
      INVALID_FISCAL_CODE: 'CODICE FISCALE NON VALIDO',
      INVALID_AGE: 'ETA NON VALIDA',
      INVALID_GENDER: 'GENERE NON VALIDO',
      INVALID_CAP: 'CAP NON VALIDO',
      MISSING_ADDRESS: 'INDIRIZZO MANCANTE',
      INVALID_TECHNICAL_DATA_VALUE: 'VALORE DATI TECNICI NON VALIDO',
      MISSING_POLICY_VARIABLE: 'VARIABILE POLIZZA MANCANTE',
      INVALID_PRODUCT: 'PRODOTTO NON VALIDO',
      INVALID_CONTRACT_STATE: 'STATO CONTRATTO NON VALIDO',
      INVALID_PAYMENT_FREQUENCY: 'FREQUENZA PAGAMENTO NON VALIDA',
      MISSING_PAYMENT_DATE: 'DATA PAGAMENTO MANCANTE',
      MISSING_PAYMENT_TYPE: 'TIPO PAGAMENTO MANCANTE',
    };
    const translatedCode = codeTranslations[code] || code;
    const error = {
      code: translatedCode,
      message,
      type,
      details,
      timestamp: new Date().toISOString(),
    };

    if (type === ERROR_TYPES.BLOCKING) {
      this.errors.push(error);
      this.isValid = false;
    } else if (type === ERROR_TYPES.WARNING) {
      this.warnings.push(error);
    } else if (type === ERROR_TYPES.SUGGESTION) {
      this.suggestions.push(error);
    }
  }

  hasBlockingErrors() {
    return this.errors.length > 0;
  }

  hasWarnings() {
    return this.warnings.length > 0;
  }

  hasSuggestions() {
    return this.suggestions.length > 0;
  }

  getAllIssues() {
    return [...this.errors, ...this.warnings, ...this.suggestions];
  }
}

// ============================================================================
// 1. GUARANTEE COMPATIBILITY VALIDATION
// ============================================================================

/**
 * Validates that selected guarantees are compatible with each other
 * @param {Array} coverages - Array of coverage objects with coverage_code
 * @returns {ValidationResult}
 */
export function validateGuaranteeCompatibility(coverages) {
  const result = new ValidationResult();
  const selectedGuarantees = coverages.map((c) => c.coverage_code);

  // Check for incompatible guarantees
  selectedGuarantees.forEach((guarantee) => {
    const incompatible = INCOMPATIBLE_GUARANTEES[guarantee] || [];

    incompatible.forEach((incompatibleGuarantee) => {
      if (selectedGuarantees.includes(incompatibleGuarantee)) {
        result.addError(
          'INCOMPATIBLE_GUARANTEES',
          `${ERROR_MESSAGES.INCOMPATIBLE_GUARANTEES(getGuaranteeDisplayName(guarantee), getGuaranteeDisplayName(incompatibleGuarantee))}`,
          ERROR_TYPES.BLOCKING,
        //   { guarantee, incompatibleWith: incompatibleGuarantee },
        );
      }
    });
  });

  // Check for duplicate guarantees
  const duplicates = selectedGuarantees.filter((item, index) => selectedGuarantees.indexOf(item) !== index);
  duplicates.forEach((duplicate) => {
    result.addError(
      'DUPLICATE_GUARANTEE',
      `${ERROR_MESSAGES.DUPLICATE_GUARANTEE}: ${getGuaranteeDisplayName(duplicate)}`,
      ERROR_TYPES.BLOCKING,
      { guarantee: duplicate },
    );
  });

  return result;
}

/**
 * Validates required guarantee combinations
 * @param {Array} coverages - Array of coverage objects
 * @returns {ValidationResult}
 */
export function validateRequiredCombinations(coverages) {
  const result = new ValidationResult();
  const selectedGuarantees = coverages.map((c) => c.coverage_code);

  selectedGuarantees.forEach((guarantee) => {
    const required = REQUIRED_GUARANTEE_COMBINATIONS[guarantee] || [];

    required.forEach((requiredGuarantee) => {
      if (Array.isArray(requiredGuarantee)) {
        const satisfied = requiredGuarantee.some((code) => selectedGuarantees.includes(code));
        if (!satisfied) {
          result.addError(
            'MISSING_REQUIRED_GUARANTEE',
            `${ERROR_MESSAGES.MISSING_REQUIRED_GUARANTEE}: ${getGuaranteeDisplayName(guarantee)} richiede ${requiredGuarantee.map(getGuaranteeDisplayName).join(' o ')}`,
            ERROR_TYPES.BLOCKING,
            { guarantee, requiredGuarantee },
          );
        }
        return;
      }

      if (!selectedGuarantees.includes(requiredGuarantee)) {
        result.addError(
          'MISSING_REQUIRED_GUARANTEE',
          `${ERROR_MESSAGES.MISSING_REQUIRED_GUARANTEE}: ${getGuaranteeDisplayName(guarantee)} richiede ${getGuaranteeDisplayName(requiredGuarantee)}`,
          ERROR_TYPES.BLOCKING,
          { guarantee, requiredGuarantee },
        );
      }
    });
  });

  // RCT is always required for any property guarantee
  if (selectedGuarantees.length > 0 && !selectedGuarantees.includes('RCT')) {
    result.addError(
      'MISSING_REQUIRED_GUARANTEE',
      `${ERROR_MESSAGES.MISSING_REQUIRED_GUARANTEE}: ${getGuaranteeDisplayName('RCT')} è obbligatoria`,
      ERROR_TYPES.BLOCKING,
      { guarantee: 'RCT', reason: 'mandatory_for_all' },
    );
  }

  return result;
}

// ============================================================================
// 2. PARAMETER VALIDATION
// ============================================================================

/**
 * Validates guarantee-specific parameters
 * @param {Array} coverages - Array of coverage objects with parameters
 * @param {number} assetNumber - Asset number (1, 2, 3)
 * @returns {ValidationResult}
 */
export function validateGuaranteeParameters(coverages, assetNumber = 1) {
  const result = new ValidationResult();

  coverages.forEach((coverage) => {
    const guaranteeCode = coverage.coverage_code;
    const parameterRules = GUARANTEE_PARAMETERS[guaranteeCode];

    if (!parameterRules) {
      return; // No parameter rules for this guarantee
    }

    Object.entries(parameterRules).forEach(([paramKey, rule]) => {
      // Check if rule is asset-specific
      if (rule.asset !== undefined) {
        const applicableAssets = Array.isArray(rule.asset) ? rule.asset : [rule.asset];
        if (!applicableAssets.includes(assetNumber)) {
          return; // Rule doesn't apply to this asset
        }
      }

      // Find parameter in coverage
      const param = coverage.parameter_collections?.find(
        (p) => p.progressivo_parametro === parseInt(paramKey) || p.key === paramKey,
      );

      // TODO: Temporarily disabled - re-enable when employee data is properly set
      //   if (rule.required && !param) {
      //     result.addError(
      //       'MISSING_REQUIRED_PARAMETER',
      //       `${ERROR_MESSAGES.MISSING_REQUIRED_PARAMETER}: ${guaranteeCode} - ${rule.description}`,
      //       ERROR_TYPES.BLOCKING,
      //       { guarantee: guaranteeCode, parameter: paramKey, description: rule.description }
      //     );
      //   }

      if (param && rule.required_value && param.value !== rule.required_value) {
        result.addError(
          'INVALID_PARAMETER_VALUE',
          `${ERROR_MESSAGES.INVALID_PARAMETER_VALUE}: ${getGuaranteeDisplayName(guaranteeCode)} - ${rule.description} deve essere '${rule.required_value}'`,
          ERROR_TYPES.BLOCKING,
          {
            guarantee: guaranteeCode, parameter: paramKey, expected: rule.required_value, actual: param.value,
          },
        );
      }

      // Special validation for n_addetti (RCT)
      // TODO: Temporarily disabled - re-enable when employee data is properly set
      // if (guaranteeCode === 'RCT' && paramKey === 'n_addetti') {
      //   const nAddetti = coverage.n_addetti || param?.value;
      //   if (!nAddetti) {
      //     result.addError(
      //       'MISSING_REQUIRED_PARAMETER',
      //       ERROR_MESSAGES.INVALID_N_ADDETTI,
      //       ERROR_TYPES.BLOCKING,
      //       { guarantee: guaranteeCode, parameter: 'n_addetti' }
      //     );
      //   } else if (nAddetti < rule.min || nAddetti > rule.max) {
      //     result.addError(
      //       'INVALID_PARAMETER_VALUE',
      //       `${ERROR_MESSAGES.INVALID_N_ADDETTI} (${nAddetti})`,
      //       ERROR_TYPES.BLOCKING,
      //       { guarantee: guaranteeCode, parameter: 'n_addetti', value: nAddetti, min: rule.min, max: rule.max }
      //     );
      //   }
      // }
    });
  });

  return result;
}

// ============================================================================
// 3. CAPITAL AMOUNT VALIDATION
// ============================================================================

/**
 * Validates capital amounts for guarantees
 * @param {Array} coverages - Array of coverage objects with capital amounts
 * @returns {ValidationResult}
 */
export function validateCapitalAmounts(coverages) {
  const result = new ValidationResult();

  coverages.forEach((coverage) => {
    const guaranteeCode = coverage.coverage_code;
    const limits = CAPITAL_LIMITS[guaranteeCode];

    if (!limits) {
      return; // No capital limits for this guarantee
    }

    const capital = coverage.capital_amount || coverage.importo_assicurato || coverage.max_amount || 0;

    // Blocking errors for min/max violations
    // TODO: Temporarily disabled - re-enable when capital amounts are properly set
    // if (capital < limits.min && limits.min > 0) {
    //   result.addError(
    //     'CAPITAL_BELOW_MIN',
    //     `${ERROR_MESSAGES.CAPITAL_BELOW_MIN}: ${getGuaranteeDisplayName(guaranteeCode)} (Min: €${limits.min.toLocaleString()}, Attuale: €${capital.toLocaleString()})`,
    //     ERROR_TYPES.BLOCKING,
    //     { guarantee: guaranteeCode, capital, min: limits.min }
    //   );
    // }

    if (capital > limits.max && limits.max > 0) {
      result.addError(
        'CAPITALE SOPRA IL MASSIMO',
        `${ERROR_MESSAGES.CAPITAL_ABOVE_MAX}: ${getGuaranteeDisplayName(guaranteeCode)} (Max: €${limits.max.toLocaleString()}, Attuale: €${capital.toLocaleString()})`,
        ERROR_TYPES.BLOCKING,
        // { guarantee: guaranteeCode, capital, max: limits.max },
      );
    }

    // Warnings for recommended ranges
    // if (capital < limits.recommended_min && capital >= limits.min && limits.recommended_min > 0) {
    //   result.addError(
    //     'CAPITAL_BELOW_RECOMMENDED',
    //     `${ERROR_MESSAGES.CAPITAL_BELOW_RECOMMENDED}: ${getGuaranteeDisplayName(guaranteeCode)} (Raccomandato: €${limits.recommended_min.toLocaleString()}, Attuale: €${capital.toLocaleString()})`,
    //     ERROR_TYPES.WARNING,
    //     { guarantee: guaranteeCode, capital, recommended_min: limits.recommended_min }
    //   );
    // }

    // if (capital > limits.recommended_max && capital <= limits.max && limits.recommended_max > 0) {
    //   result.addError(
    //     'CAPITAL_ABOVE_RECOMMENDED',
    //     `${ERROR_MESSAGES.CAPITAL_ABOVE_RECOMMENDED}: ${getGuaranteeDisplayName(guaranteeCode)} (Raccomandato: €${limits.recommended_max.toLocaleString()}, Attuale: €${capital.toLocaleString()})`,
    //     ERROR_TYPES.WARNING,
    //     { guarantee: guaranteeCode, capital, recommended_max: limits.recommended_max }
    //   );
    // }
  });

  return result;
}

// ============================================================================
// 4. ASSET-SPECIFIC VALIDATION
// ============================================================================

/**
 * Validates guarantees against asset-specific rules
 * @param {Array} coverages - Array of coverage objects
 * @param {number} assetNumber - Asset number (1, 2, 3)
 * @param {Object} assetData - Asset data (property type, location, etc.)
 * @returns {ValidationResult}
 */
export function validateAssetGuarantees(coverages, assetNumber, assetData = {}) {
  const result = new ValidationResult();
  const assetRules = ASSET_RULES[assetNumber];

  if (!assetRules) {
    return result; // No rules for this asset
  }

  const selectedGuarantees = coverages.map((c) => c.coverage_code);

  // Check property type
  if (assetData.property_type && assetData.property_type !== assetRules.property_type) {
    result.addError(
      'INVALID_PROPERTY_TYPE',
      `${ERROR_MESSAGES.INVALID_PROPERTY_TYPE}: Asset ${assetNumber} deve essere tipo '${assetRules.property_type}'`,
      ERROR_TYPES.BLOCKING,
      { asset: assetNumber, expected: assetRules.property_type, actual: assetData.property_type },
    );
  }

  // Check required guarantees for asset
  assetRules.required_guarantees.forEach((requiredGuarantee) => {
    if (!selectedGuarantees.includes(requiredGuarantee)) {
      result.addError(
        'ASSET_MISSING_REQUIRED_GUARANTEE',
        `${ERROR_MESSAGES.ASSET_MISSING_REQUIRED_GUARANTEE}: Asset ${assetNumber} richiede ${getGuaranteeDisplayName(requiredGuarantee)}`,
        ERROR_TYPES.BLOCKING,
        { asset: assetNumber, guarantee: requiredGuarantee },
      );
    }
  });

  // Check allowed guarantees
  selectedGuarantees.forEach((guarantee) => {
    if (!assetRules.allowed_guarantees.includes(guarantee)) {
      result.addError(
        'ASSET_GUARANTEE_NOT_ALLOWED',
        `${ERROR_MESSAGES.ASSET_GUARANTEE_NOT_ALLOWED}: ${getGuaranteeDisplayName(guarantee)} non è consentita per Asset ${assetNumber}`,
        ERROR_TYPES.BLOCKING,
        { asset: assetNumber, guarantee },
      );
    }
  });

  // Validate asset-specific capital limits
  coverages.forEach((coverage) => {
    const guaranteeCode = coverage.coverage_code;
    const assetCapitalLimits = assetRules.capital_limits?.[guaranteeCode];

    if (assetCapitalLimits) {
      const capital = coverage.capital_amount || coverage.importo_assicurato || 0;

      if (capital < assetCapitalLimits.min) {
        result.addError(
          'CAPITAL_BELOW_MIN',
          `${ERROR_MESSAGES.CAPITAL_BELOW_MIN}: Asset ${assetNumber} - ${getGuaranteeDisplayName(guaranteeCode)} (Min: €${assetCapitalLimits.min.toLocaleString()})`,
          ERROR_TYPES.BLOCKING,
          {
            asset: assetNumber, guarantee: guaranteeCode, capital, min: assetCapitalLimits.min,
          },
        );
      }

      if (capital > assetCapitalLimits.max) {
        result.addError(
          'CAPITAL_ABOVE_MAX',
          `${ERROR_MESSAGES.CAPITAL_ABOVE_MAX}: Asset ${assetNumber} - ${getGuaranteeDisplayName(guaranteeCode)} (Max: €${assetCapitalLimits.max.toLocaleString()})`,
          ERROR_TYPES.BLOCKING,
          {
            asset: assetNumber, guarantee: guaranteeCode, capital, max: assetCapitalLimits.max,
          },
        );
      }
    }
  });

  return result;
}

// ============================================================================
// 5. TEMPORAL VALIDATION
// ============================================================================

/**
 * Validates date-related fields
 * @param {Object} formData - Form data with dates
 * @returns {ValidationResult}
 */
export function validateTemporalConstraints(formData) {
  const result = new ValidationResult();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // dt_effetto >= Today
  if (formData.effective_date) {
    const effectiveDate = new Date(formData.effective_date);
    effectiveDate.setHours(0, 0, 0, 0);

    if (effectiveDate < today) {
      result.addError(
        'INVALID_EFFECTIVE_DATE',
        ERROR_MESSAGES.INVALID_EFFECTIVE_DATE,
        ERROR_TYPES.WARNING,
        { effective_date: formData.effective_date },
      );
    }

    // dt_scadenza = dt_effetto + 1 year
    if (formData.expiry_date) {
      const expiryDate = new Date(formData.expiry_date);
      const expectedExpiry = new Date(effectiveDate);
      expectedExpiry.setFullYear(expectedExpiry.getFullYear() + 1);

      if (expiryDate.getTime() !== expectedExpiry.getTime()) {
        result.addError(
          'INVALID_EXPIRY_DATE',
          ERROR_MESSAGES.INVALID_EXPIRY_DATE,
          ERROR_TYPES.WARNING,
          { effective_date: formData.effective_date, expiry_date: formData.expiry_date, expected: expectedExpiry.toISOString() },
        );
      }
    }
  }

  // dt_inclusione <= dt_effetto
  if (formData.inclusion_date && formData.effective_date) {
    const inclusionDate = new Date(formData.inclusion_date);
    const effectiveDate = new Date(formData.effective_date);

    if (inclusionDate > effectiveDate) {
      result.addError(
        'INVALID_INCLUSION_DATE',
        ERROR_MESSAGES.INVALID_INCLUSION_DATE,
        ERROR_TYPES.WARNING,
        { inclusion_date: formData.inclusion_date, effective_date: formData.effective_date },
      );
    }
  }

  // dt_esclusione >= dt_inclusione
  if (formData.exclusion_date && formData.inclusion_date) {
    const exclusionDate = new Date(formData.exclusion_date);
    const inclusionDate = new Date(formData.inclusion_date);

    if (exclusionDate < inclusionDate) {
      result.addError(
        'INVALID_EXCLUSION_DATE',
        ERROR_MESSAGES.INVALID_EXCLUSION_DATE,
        ERROR_TYPES.WARNING,
        { exclusion_date: formData.exclusion_date, inclusion_date: formData.inclusion_date },
      );
    }
  }

  return result;
}

// ============================================================================
// 6. ANAGRAPHIC VALIDATION
// ============================================================================

/**
 * Validates Italian fiscal code format
 * @param {string} fiscalCode - Fiscal code to validate
 * @returns {boolean}
 */
export function validateFiscalCode(fiscalCode) {
  if (!fiscalCode || fiscalCode.length !== 16) {
    return false;
  }

  const regex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
  return regex.test(fiscalCode.toUpperCase());
}

/**
 * Validates anagraphic data (person information)
 * @param {Object} personData - Person data object
 * @returns {ValidationResult}
 */
export function validateAnagraphicData(personData) {
  const result = new ValidationResult();

  // Fiscal code validation
  if (personData.fiscal_code && !validateFiscalCode(personData.fiscal_code)) {
    result.addError(
      'INVALID_FISCAL_CODE',
      ERROR_MESSAGES.INVALID_FISCAL_CODE,
      ERROR_TYPES.BLOCKING,
      { fiscal_code: personData.fiscal_code },
    );
  }

  // Age validation (>= 18 years)
  if (personData.date_of_birth) {
    const birthDate = new Date(personData.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    if (age < 18) {
      result.addError(
        'INVALID_AGE',
        ERROR_MESSAGES.INVALID_AGE,
        ERROR_TYPES.BLOCKING,
        { age, date_of_birth: personData.date_of_birth },
      );
    }
  }

  // Gender validation
  if (personData.gender && !['M', 'F', 'G'].includes(personData.gender)) {
    result.addError(
      'INVALID_GENDER',
      ERROR_MESSAGES.INVALID_GENDER,
      ERROR_TYPES.BLOCKING,
      { gender: personData.gender },
    );
  }

  return result;
}

/**
 * Validates address data
 * @param {Object} addressData - Address data object
 * @returns {ValidationResult}
 */
export function validateAddressData(addressData) {
  const result = new ValidationResult();

  // CAP validation for Italy
  if (addressData.country_code_uic === '086' || !addressData.country_code_uic) {
    if (addressData.postal_code && !/^\d{5}$/.test(addressData.postal_code)) {
      result.addError(
        'INVALID_CAP',
        ERROR_MESSAGES.INVALID_CAP,
        ERROR_TYPES.BLOCKING,
        { postal_code: addressData.postal_code },
      );
    }
  }

  // Required fields
  if (!addressData.address || addressData.address.trim() === '') {
    result.addError(
      'MISSING_ADDRESS',
      'Indirizzo obbligatorio',
      ERROR_TYPES.BLOCKING,
      { field: 'address' },
    );
  }

  return result;
}

// ============================================================================
// 7. TECHNICAL DATA VALIDATION
// ============================================================================

/**
 * Validates technical data for property
 * @param {Object} technicalData - Technical data object
 * @returns {ValidationResult}
 */
// export function validateTechnicalData(technicalData) {
//   const result = new ValidationResult();

//   REQUIRED_TECHNICAL_DATA.forEach((requirement) => {
//     // const value = technicalData[`${requirement.cod_elenco_variabile.toLowerCase()}_value`];

//     // TODO: Temporarily disabled - re-enable when employee data is properly set
//     // if (!value) {
//     //   result.addError(
//     //     'MISSING_TECHNICAL_DATA',
//     //     `${ERROR_MESSAGES.MISSING_TECHNICAL_DATA}: ${requirement.description}`,
//     //     ERROR_TYPES.BLOCKING,
//     //     { field: requirement.cod_elenco_variabile, description: requirement.description }
//     //   );
//     // }

//     // Validate specific required values
//     // if (requirement.required_value && value !== requirement.required_value) {
//     //   result.addError(
//     //     'INVALID_TECHNICAL_DATA_VALUE',
//     //     `${ERROR_MESSAGES.INVALID_TECHNICAL_DATA_VALUE}: ${requirement.description} deve essere '${requirement.required_value}'`,
//     //     ERROR_TYPES.BLOCKING,
//     //     { field: requirement.cod_elenco_variabile, expected: requirement.required_value, actual: value }
//     //   );
//     // }

//     // Validate year types
//     // if (requirement.type === 'year' && value) {
//     //   const year = parseInt(value);
//     //   const currentYear = new Date().getFullYear();

//     //   if (year || year < 1800 || year > currentYear) {
//     //     result.addError(
//     //       'INVALID_TECHNICAL_DATA_VALUE',
//     //       `${ERROR_MESSAGES.INVALID_TECHNICAL_DATA_VALUE}: ${requirement.description} deve essere un anno valido`,
//     //       ERROR_TYPES.BLOCKING,
//     //       {
//     //         field: requirement.cod_elenco_variabile, value, min: 1800, max: currentYear,
//     //       },
//     //     );
//     //   }
//     // }
//   });

//   return result;
// }

/**
 * Validates policy variables
 * @param {Array} policyVariables - Array of policy variable objects
 * @returns {ValidationResult}
 */
export function validatePolicyVariables(policyVariables) {
  const result = new ValidationResult();

  REQUIRED_POLICY_VARIABLES.forEach((requirement) => {
    const variable = policyVariables?.find(
      (v) => v.cod_elenco_variabile === requirement.cod_elenco_variabile,
    );

    if (!variable) {
      result.addError(
        'MISSING_POLICY_VARIABLE',
        `${ERROR_MESSAGES.MISSING_POLICY_VARIABLE}: ${requirement.description}`,
        ERROR_TYPES.BLOCKING,
        { variable: requirement.cod_elenco_variabile, description: requirement.description },
      );
    }

    // ATTECO is required and must have a value
    if (requirement.required && requirement.cod_elenco_variabile === 'ATTECO') {
      if (!variable?.valore_stringa) {
        result.addError(
          'MISSING_POLICY_VARIABLE',
          `${ERROR_MESSAGES.MISSING_POLICY_VARIABLE}: Codice ATECO obbligatorio`,
          ERROR_TYPES.BLOCKING,
          { variable: 'ATTECO', description: 'Codice Ateco' },
        );
      }
    }
  });

  return result;
}

// ============================================================================
// 8. CONTRACTUAL VALIDATION
// ============================================================================

/**
 * Validates contract-level fields
 * @param {Object} contractData - Contract data object
 * @returns {ValidationResult}
 */
export function validateContractData(contractData) {
  const result = new ValidationResult();

  // Product validation
  if (contractData.product_code && contractData.product_code !== CONTRACT_CONSTANTS.product_code) {
    result.addError(
      'INVALID_PRODUCT',
      `${ERROR_MESSAGES.INVALID_PRODUCT}: Previsto ${CONTRACT_CONSTANTS.product_code}, ricevuto ${contractData.product_code}`,
      ERROR_TYPES.BLOCKING,
      { expected: CONTRACT_CONSTANTS.product_code, actual: contractData.product_code },
    );
  }

  // Contract state validation
  if (contractData.stato_contratto && !CONTRACT_CONSTANTS.valid_states.includes(contractData.stato_contratto)) {
    result.addError(
      'INVALID_CONTRACT_STATE',
      ERROR_MESSAGES.INVALID_CONTRACT_STATE,
      ERROR_TYPES.BLOCKING,
      { state: contractData.stato_contratto, valid_states: CONTRACT_CONSTANTS.valid_states },
    );
  }

  // Payment frequency validation
  if (contractData.payment_frequency && contractData.payment_frequency !== 2) {
    result.addError(
      'INVALID_PAYMENT_FREQUENCY',
      `${ERROR_MESSAGES.INVALID_PAYMENT_FREQUENCY}: Deve essere annuale (2)`,
      ERROR_TYPES.BLOCKING,
      { payment_frequency: contractData.payment_frequency },
    );
  }

  return result;
}

// ============================================================================
// 9. BUSINESS LOGIC VALIDATION
// ============================================================================

/**
 * Validates business logic rules
 * @param {Object} formData - Complete form data
 * @returns {ValidationResult}
 */
export function validateBusinessLogic(formData) {
  const result = new ValidationResult();

  // Payment/Collection validation
  if (formData.incassa_polizza === 'S') {
    if (!formData.dt_incasso) {
      result.addError(
        'MISSING_PAYMENT_DATE',
        ERROR_MESSAGES.MISSING_PAYMENT_DATE,
        ERROR_TYPES.BLOCKING,
        { incassa_polizza: formData.incassa_polizza },
      );
    }

    if (!formData.fig_tipo_incasso) {
      result.addError(
        'MISSING_PAYMENT_TYPENTE',
        ERROR_MESSAGES.MISSING_PAYMENT_TYPE,
        ERROR_TYPES.BLOCKING,
        { incassa_polizza: formData.incassa_polizza },
      );
    }
  }

  // Number of employees validation (for RCT)
  // TODO: Temporarily disabled - re-enable when employee data is properly set
  // const rctCoverage = formData.coverages?.find(c => c.coverage_code === 'RCT');
  // if (rctCoverage && !formData.number_of_employees) {
  //   result.addError(
  //     'MISSING_REQUIRED_PARAMETER',
  //     'Numero di dipendenti obbligatorio per RCT',
  //     ERROR_TYPES.BLOCKING,
  //     { guarantee: 'RCT', field: 'number_of_employees' }
  //   );
  // }

  return result;
}

// ============================================================================
// 10. MASTER VALIDATION FUNCTION
// ============================================================================

/**
 * Performs comprehensive validation of all Product 95 rules
 * @param {Object} formData - Complete form data
 * @param {Object} options - Validation options
 * @returns {ValidationResult}
 */
/**
 * Validates only the guarantee-related rules (compatibility, required
 * combinations, parameters) — no address/anagraphic/contractual/temporal
 * checks. Use this to gate leaving the guarantee-selection step, since
 * contact/personal data hasn't necessarily been collected yet at that
 * point in the wizard.
 * @param {Object} formData
 * @param {Object} options
 * @returns {ValidationResult}
 */
export function validateProduct95Guarantees(formData, options = {}) {
  const result = new ValidationResult();

  // 1. Guarantee Compatibility
  const compatibilityResult = validateGuaranteeCompatibility(formData.coverages || []);
  result.errors.push(...compatibilityResult.errors);
  result.warnings.push(...compatibilityResult.warnings);
  result.suggestions.push(...compatibilityResult.suggestions);

  // 2. Required Combinations
  const combinationsResult = validateRequiredCombinations(formData.coverages || []);
  result.errors.push(...combinationsResult.errors);
  result.warnings.push(...combinationsResult.warnings);

  // 3. Parameters
  const parametersResult = validateGuaranteeParameters(formData.coverages || [], options.assetNumber || 1);
  result.errors.push(...parametersResult.errors);
  result.warnings.push(...parametersResult.warnings);

  return result;
}

export function validateProduct95Complete(formData, options = {}) {
  const masterResult = new ValidationResult();

  // 1-3. Guarantee Compatibility, Required Combinations, Parameters
  const guaranteesResult = validateProduct95Guarantees(formData, options);
  masterResult.errors.push(...guaranteesResult.errors);
  masterResult.warnings.push(...guaranteesResult.warnings);
  masterResult.suggestions.push(...guaranteesResult.suggestions);

  // TODO: Temporarily disabled - re-enable when employee data is properly set
  // 4. Capital Amounts
  //   const capitalsResult = validateCapitalAmounts(formData.coverages || []);
  //   masterResult.errors.push(...capitalsResult.errors);
  //   masterResult.warnings.push(...capitalsResult.warnings);

  // 5. Asset-specific
  if (options.validateAssets) {
    const assetResult = validateAssetGuarantees(
      formData.coverages || [],
      options.assetNumber || 1,
      options.assetData || {},
    );
    masterResult.errors.push(...assetResult.errors);
    masterResult.warnings.push(...assetResult.warnings);
  }

  // 6. Temporal
  const temporalResult = validateTemporalConstraints(formData);
  masterResult.errors.push(...temporalResult.errors);
  masterResult.warnings.push(...temporalResult.warnings);

  // 7. Anagraphic
  if (formData.gender !== 'G') {
    const anagraphicResult = validateAnagraphicData(formData);
    masterResult.errors.push(...anagraphicResult.errors);
    masterResult.warnings.push(...anagraphicResult.warnings);
  }

  // 8. Address
  if (formData.address || formData.site_address) {
    const addressResult = validateAddressData(formData.address || formData.site_address);
    masterResult.errors.push(...addressResult.errors);
    masterResult.warnings.push(...addressResult.warnings);
  }

  // 9. Technical Data
  //   if (formData.technical_location_data) {
  //     const technicalResult = validateTechnicalData(formData.technical_location_data);
  //     masterResult.errors.push(...technicalResult.errors);
  //     masterResult.warnings.push(...technicalResult.warnings);
  //   }

  // 10. Contractual
  const contractResult = validateContractData(formData);
  masterResult.errors.push(...contractResult.errors);
  masterResult.warnings.push(...contractResult.warnings);

  // 11. Business Logic
  const businessResult = validateBusinessLogic(formData);
  masterResult.errors.push(...businessResult.errors);
  masterResult.warnings.push(...businessResult.warnings);

  // Update isValid flag
  masterResult.isValid = masterResult.errors.length === 0;

  return masterResult;
}

// ============================================================================
// REAL-TIME INPUT VALIDATION HELPERS
// ============================================================================

/**
 * Validates capital amount for a specific guarantee in real-time.
 * Use this function for inline input validation as user types.
 *
 * @param {string} guaranteeCode - The guarantee code (e.g., 'CONT.F', 'REFR-8', 'RILO')
 * @param {number|string} amount - The capital amount entered by user
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateCapitalInput(guaranteeCode, amount) {
  // Convert to number
  const numericAmount = typeof amount === 'string'
    ? parseFloat(amount.replace(/[^\d]/g, ''))
    : amount;

  // If empty or NaN, no validation error (let required validation handle empty)
  if (!numericAmount || Number.isNaN(numericAmount)) {
    return { isValid: true, error: null };
  }

  // Get limits for this guarantee code
  const limits = CAPITAL_LIMITS[guaranteeCode];

  // If no limits defined for this guarantee, it's valid
  if (!limits) {
    return { isValid: true, error: null };
  }

  // Skip validation for guarantees with 0 limits (like RCT, INCI)
  if (limits.min === 0 && limits.max === 0) {
    return { isValid: true, error: null };
  }

  // Check minimum
  if (numericAmount < limits.min) {
    return {
      isValid: false,
      error: `Importo minimo: €${limits.min.toLocaleString('it-IT')}`,
    };
  }

  // Check maximum
  if (numericAmount > limits.max) {
    return {
      isValid: false,
      error: `Importo massimo: €${limits.max.toLocaleString('it-IT')}`,
    };
  }

  return { isValid: true, error: null };
}

/**
 * Gets the capital limits for a specific guarantee code.
 * Useful for displaying min/max hints in the UI.
 *
 * @param {string} guaranteeCode - The guarantee code
 * @returns {Object|null} { min, max, recommended_min, recommended_max } or null if not found
 */
export function getCapitalLimits(guaranteeCode) {
  return CAPITAL_LIMITS[guaranteeCode] || null;
}

/**
 * Checks if a guarantee is incompatible with other selected guarantees.
 * Use this function to validate when user selects/adds a new guarantee.
 *
 * @param {string} guaranteeCode - The guarantee code being checked
 * @param {Array} allSelectedGuarantees - Array of all selected guarantee codes (including the one being checked)
 * @returns {Object} { isValid: boolean, error: string|null, incompatibleWith: string[] }
 */
export function validateIncompatibleGuarantees(guaranteeCode, allSelectedGuarantees) {
  if (!guaranteeCode || !allSelectedGuarantees || allSelectedGuarantees.length === 0) {
    return { isValid: true, error: null, incompatibleWith: [] };
  }

  // Get incompatible list for this guarantee
  const incompatibleList = INCOMPATIBLE_GUARANTEES[guaranteeCode] || [];

  if (incompatibleList.length === 0) {
    return { isValid: true, error: null, incompatibleWith: [] };
  }

  // Find which incompatible guarantees are also selected
  const conflictingGuarantees = allSelectedGuarantees.filter(
    (code) => code !== guaranteeCode && incompatibleList.includes(code),
  );

  if (conflictingGuarantees.length > 0) {
    // Get display names for better error messages
    const conflictingNames = conflictingGuarantees.map((code) => {
      const found = commercial_goods.find((g) => g.id === code);
      return found ? found.name : code;
    });

    return {
      isValid: false,
      error: `Garanzia - ${getGuaranteeDisplayName(guaranteeCode)} non acquistabile con garanzia ${conflictingNames.join(', ')}`,
      incompatibleWith: conflictingGuarantees,
    };
  }

  return { isValid: true, error: null, incompatibleWith: [] };
}

/**
 * Gets the list of incompatible guarantee codes for a specific guarantee.
 *
 * @param {string} guaranteeCode - The guarantee code
 * @returns {string[]} Array of incompatible guarantee codes
 */
export function getIncompatibleGuarantees(guaranteeCode) {
  return INCOMPATIBLE_GUARANTEES[guaranteeCode] || [];
}

// ============================================================================
// EXPORT ALL VALIDATION FUNCTIONS
// ============================================================================

export default {
  validateGuaranteeCompatibility,
  validateRequiredCombinations,
  validateGuaranteeParameters,
  validateCapitalAmounts,
  validateAssetGuarantees,
  validateTemporalConstraints,
  validateAnagraphicData,
  validateAddressData,
  //   validateTechnicalData,
  validatePolicyVariables,
  validateContractData,
  validateBusinessLogic,
  validateProduct95Complete,
  validateCapitalInput,
  getCapitalLimits,
  validateIncompatibleGuarantees,
  getIncompatibleGuarantees,
  ValidationResult,
};
